import { type ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api } from '@/lib/api';

type Backup = { name: string; size: number; at: string };
type Status = {
  running: boolean;
  intervalSec: number;
  inProgress: boolean;
  lastRunAt: string | null;
  lastOk: boolean | null;
  lastMessage: string | null;
  lastDurationMs: number | null;
  total: number;
  recent: Backup[];
  s3Enabled: boolean;
  lastS3Ok: boolean | null;
  lastS3Message: string | null;
};

type ResetOverview = {
  currentEvent: { id: number; name: string } | null;
  adminCount: number;
  gameCount: number;
  counts: {
    orders: number;
    customers: number;
    products: number;
    lines: number;
    reps: number;
    rewardClaims: number;
    promotions: number;
  };
};

const taille = (o: number) =>
  o >= 1024 * 1024 ? `${(o / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`;

const intervalLabel = (s: number) => (s < 60 ? `${s} s` : `${s / 60} min`);

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

/** Panneau d'administration : sauvegardes, restauration, réinitialisation du show. */
export default function Settings() {
  const qc = useQueryClient();
  const status = useQuery<Status>({
    queryKey: ['admin', 'backup'],
    queryFn: () => api('/api/admin/backup'),
    refetchInterval: 5000, // statut rafraîchi automatiquement
  });
  const s = status.data;
  const [intervalSec, setIntervalSec] = useState(60);

  const put = (d: Status) => qc.setQueryData(['admin', 'backup'], d);
  const start = useMutation({
    mutationFn: () =>
      api<Status>('/api/admin/backup/start', { method: 'POST', body: JSON.stringify({ intervalSec }) }),
    onSuccess: put,
  });
  const stop = useMutation({
    mutationFn: () => api<Status>('/api/admin/backup/stop', { method: 'POST' }),
    onSuccess: put,
  });
  const run = useMutation({
    mutationFn: () => api<Status>('/api/admin/backup/run', { method: 'POST' }),
    onSuccess: put,
  });

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Paramètres</h2>

      <IpadQrSection />

      <GiftEmailsSection />

      <GiftsCloseSection />

      <section className="bg-card space-y-4 rounded-xl border p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Sauvegardes automatiques</h3>
            <p className="text-muted-foreground text-sm">
              Copie complète de la base, non bloquante — les reps ne sont pas ralentis.
            </p>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              s?.running
                ? 'bg-green-600/15 text-green-700 dark:text-green-400'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {s?.running ? `● Actif — toutes les ${intervalLabel(s.intervalSec)}` : '○ Arrêté'}
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1.5">
            <span className="block text-sm font-medium">Intervalle</span>
            <select
              value={s?.running ? s.intervalSec : intervalSec}
              onChange={(e) => setIntervalSec(Number(e.target.value))}
              disabled={s?.running}
              className="border-input bg-background h-11 rounded-lg border px-3 disabled:opacity-60"
            >
              <option value={30}>30 secondes</option>
              <option value={60}>1 minute</option>
              <option value={300}>5 minutes</option>
            </select>
          </label>

          {s?.running ? (
            <button
              onClick={() => stop.mutate()}
              disabled={stop.isPending}
              className="border-destructive/40 text-destructive h-11 rounded-lg border px-5 font-medium disabled:opacity-40"
            >
              Arrêter
            </button>
          ) : (
            <button
              onClick={() => start.mutate()}
              disabled={start.isPending}
              className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
            >
              Démarrer
            </button>
          )}

          <button
            onClick={() => run.mutate()}
            disabled={run.isPending}
            className="border-input h-11 rounded-lg border px-5 text-sm font-medium disabled:opacity-40"
          >
            Sauvegarder maintenant
          </button>
        </div>

        {s?.lastRunAt && (
          <div
            className={`rounded-lg border p-3 text-sm ${
              s.lastOk ? 'border-green-600/30 bg-green-600/5' : 'border-destructive/40 bg-destructive/5'
            }`}
          >
            <p>
              Dernière sauvegarde : <b>{new Date(s.lastRunAt).toLocaleTimeString('fr-CA')}</b>
              {s.lastDurationMs != null && (
                <span className="text-muted-foreground"> · {s.lastDurationMs} ms</span>
              )}
              {s.inProgress && <span className="text-muted-foreground"> · en cours…</span>}
            </p>
            {s.lastOk === false && <p className="text-destructive mt-1">Échec : {s.lastMessage}</p>}
            {s.s3Enabled && (
              <p className="mt-1">
                Copie hors-site (S3) :{' '}
                {s.lastS3Ok == null ? (
                  <span className="text-muted-foreground">en attente…</span>
                ) : s.lastS3Ok ? (
                  <span className="text-green-700 dark:text-green-400">✓ enregistrée</span>
                ) : (
                  <span className="text-destructive">✗ {s.lastS3Message}</span>
                )}
              </p>
            )}
          </div>
        )}

        <p className="text-muted-foreground text-xs">
          {s ? `${s.total} sauvegarde(s) conservée(s)` : 'Chargement…'} — l'application garde ~24 h
          d'historique et supprime automatiquement les plus anciennes
          {s?.s3Enabled && <> et copie chaque sauvegarde dans le S3 privé (« Backup SuperShow »)</>}.
        </p>
      </section>

      <details className="bg-card group rounded-xl border">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-6 [&::-webkit-details-marker]:hidden">
          <h3 className="font-semibold">
            Sauvegardes récentes
            {s && s.recent.length > 0 && (
              <span className="text-muted-foreground ml-2 text-sm font-normal">
                ({s.recent.length})
              </span>
            )}
          </h3>
          <span className="text-muted-foreground transition-transform group-open:rotate-180">▾</span>
        </summary>
        <div className="px-6 pb-6">
          {!s || s.recent.length === 0 ? (
            <p className="text-muted-foreground text-sm">Aucune sauvegarde pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left">
                  <th className="p-2 font-medium">Fichier</th>
                  <th className="p-2 font-medium">Quand</th>
                  <th className="p-2 font-medium">Taille</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {s.recent.map((b) => (
                  <tr key={b.name} className="border-t">
                    <td className="p-2 font-mono text-xs">{b.name}</td>
                    <td className="text-muted-foreground p-2 whitespace-nowrap">
                      {new Date(b.at).toLocaleString('fr-CA')}
                    </td>
                    <td className="text-muted-foreground p-2 tabular-nums">{taille(b.size)}</td>
                    <td className="p-2 text-right">
                      <a
                        href={`/api/admin/backup/download/${b.name}`}
                        download
                        className="text-primary text-sm font-medium"
                      >
                        Télécharger
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </div>
      </details>

      {/* --- Zone de danger : restauration + réinitialisation (repliable) ------ */}
      <details className="border-destructive/40 group rounded-xl border-2">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-6 [&::-webkit-details-marker]:hidden">
          <h3 className="text-destructive font-semibold">Zone de danger</h3>
          <span className="text-destructive/70 transition-transform group-open:rotate-180">▾</span>
        </summary>
        <div className="space-y-6 px-6 pb-6">
          <p className="text-muted-foreground text-sm">
            Opérations irréversibles. Une sauvegarde de sécurité est prise automatiquement avant
            chaque opération — rien n'est jamais perdu sans filet.
          </p>

          <RestoreSection backups={s?.recent ?? []} />
        <div className="border-t" />
        <VisitorsResetSection />
        <div className="border-t" />
        <PurgeSection
          titre="Purger le catalogue de produits"
          endpoint="/api/admin/catalogue/purge"
          queryKey="catalogue-purge"
          unite="produit"
        >
          Vide le catalogue pour repartir d'une nouvelle liste (Imports → Produits). Les produits{' '}
          <b>jamais commandés</b> sont supprimés ; ceux <b>déjà présents dans une commande</b> sont{' '}
          <b>désactivés</b> (l'historique reste intact) et seront <b>réactivés</b> s'ils reviennent
          dans un import.
        </PurgeSection>
        <div className="border-t" />
        <PurgeSection
          titre="Purger la liste des clients"
          endpoint="/api/admin/customers/purge"
          queryKey="customers-purge"
          unite="client"
        >
          Vide la liste des clients pour repartir d'une nouvelle liste (Imports → Clients). Les
          clients <b>sans commande</b> sont supprimés (leurs courriels et cadeaux/coupons remis
          partent avec) ; ceux qui <b>ont des commandes</b> sont <b>désactivés</b> (masqués,
          historique intact) et <b>réactivés</b> s'ils reviennent dans un import. Les décomptes de
          visiteurs (étiquettes) sont conservés.
        </PurgeSection>
          <div className="border-t" />
          <ResetSection />
        </div>
      </details>
    </div>
  );
}

/**
 * Clôture des cadeaux (fin du show) : bascule qui bloque toute NOUVELLE remise au
 * comptoir. Les remises déjà faites et le rapport ne sont pas touchés. Réversible.
 */
// « AAAA-MM-JJ » (fuseau du show) d'une date ISO, pour préremplir le champ date.
const isoToShowDate = (iso: string | null): string =>
  iso
    ? new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(iso))
    : '';

function GiftsCloseSection() {
  const qc = useQueryClient();
  const status = useQuery<{ closed: boolean; eventName: string | null; closedAt: string | null }>({
    queryKey: ['admin', 'gifts-status'],
    queryFn: () => api('/api/admin/gifts/status'),
  });
  const [err, setErr] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const closed = status.data?.closed ?? false;
  const closedAt = status.data?.closedAt ?? null;

  // Préremplit le champ avec la coupure actuelle (ou aujourd'hui si aucune).
  useEffect(() => {
    if (!status.data) return;
    setDate(isoToShowDate(status.data.closedAt) || isoToShowDate(new Date().toISOString()));
  }, [status.data]);

  const mutate = useMutation({
    mutationFn: (body: { closed: boolean; closedAt?: string }) =>
      api<{ closed: boolean }>('/api/admin/gifts/close', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setErr(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'gifts-status'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const dateFr = closedAt
    ? new Date(closedAt).toLocaleDateString('fr-CA', {
        timeZone: 'America/Toronto',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <section className="bg-card rounded-xl border p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">Clôture des cadeaux</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Après le show, <b>clôturez les cadeaux</b> : plus aucune nouvelle remise possible au
            comptoir — les ventes qui rentrent encore n'obtiennent <b>pas</b> de cadeaux. Les remises
            déjà faites et le rapport restent intacts. Réversible.
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-sm font-medium ${
            closed
              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
              : 'bg-green-600/15 text-green-700 dark:text-green-400'
          }`}
        >
          {status.isPending ? '…' : closed ? '● Cadeaux clôturés' : '○ Cadeaux ouverts'}
        </span>
      </div>

      {/* Date de coupure : les commandes soumises À PARTIR de ce jour ne comptent
          plus pour les cadeaux (comptoir + rapport « Cadeaux non réclamés »). */}
      <div className="border-input mt-4 flex flex-wrap items-end gap-4 rounded-lg border p-3">
        <div className="flex flex-col">
          <label htmlFor="gifts-cutoff" className="text-muted-foreground mb-1 text-sm font-medium">
            Clôturés à partir du
          </label>
          <input
            id="gifts-cutoff"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border-input rounded-md border px-2 py-1"
          />
        </div>
        <p className="text-muted-foreground w-full text-sm">
          Les commandes soumises <b>ce jour-là ou après</b> ne comptent pas pour les cadeaux.
          {closed && dateFr && (
            <>
              {' '}
              Coupure actuelle : <b>{dateFr}</b>.
            </>
          )}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        {closed ? (
          <>
            <button
              type="button"
              onClick={() => {
                if (!date) return;
                mutate.mutate({ closed: true, closedAt: date });
              }}
              disabled={mutate.isPending || !date}
              className="bg-primary h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
            >
              {mutate.isPending ? 'Enregistrement…' : 'Mettre à jour la date de coupure'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!window.confirm('Rouvrir les cadeaux ? Les remises redeviennent possibles.'))
                  return;
                mutate.mutate({ closed: false });
              }}
              disabled={mutate.isPending}
              className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
            >
              Rouvrir les cadeaux
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              if (!date) return;
              if (
                !window.confirm(
                  'Clôturer les cadeaux ? Plus aucune nouvelle remise ne sera possible au comptoir.',
                )
              )
                return;
              mutate.mutate({ closed: true, closedAt: date });
            }}
            disabled={mutate.isPending || !date}
            className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
          >
            {mutate.isPending ? 'Enregistrement…' : 'Clôturer les cadeaux'}
          </button>
        )}
      </div>
      {err && <p className="text-destructive mt-2 text-sm">{err}</p>}
    </section>
  );
}

/**
 * Envoi EN LOT des courriels de cadeaux (fin du show) : chaque client ayant validé
 * ses choix reçoit un courriel avec la dernière version de ses cadeaux.
 */
function GiftEmailsSection() {
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const envoi = useMutation({
    mutationFn: () =>
      api<{ sent: number; skipped: number; failed: number; total: number }>(
        '/api/claims/send-emails',
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      setErr(null);
      setMsg(
        `${r.sent} courriel(s) envoyé(s), ${r.skipped} ignoré(s)` +
          `${r.failed ? `, ${r.failed} échec(s)` : ''} — sur ${r.total} client(s) confirmé(s).`,
      );
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <section className="bg-card rounded-xl border p-6">
      <h3 className="font-semibold">Envoi des cadeaux par courriel</h3>
      <p className="text-muted-foreground mt-1 text-sm">
        À faire <b>à la fin du show</b> : envoie à chaque client ayant <b>validé ses choix</b> au
        comptoir un courriel avec la <b>dernière version</b> de ses cadeaux. Les clients déjà
        envoyés, sans changement depuis, sont ignorés — vous pouvez donc le relancer sans risque de
        doublon.
      </p>
      <button
        type="button"
        onClick={() => {
          if (!window.confirm('Envoyer les courriels de cadeaux à tous les clients confirmés ?'))
            return;
          setMsg(null);
          envoi.mutate();
        }}
        disabled={envoi.isPending}
        className="bg-primary text-primary-foreground mt-4 h-11 rounded-lg px-5 font-medium disabled:opacity-40"
      >
        {envoi.isPending ? 'Envoi en cours…' : 'Envoyer les courriels'}
      </button>
      {msg && <p className="mt-2 text-sm font-medium text-green-700 dark:text-green-400">{msg}</p>}
      {err && <p className="text-destructive mt-2 text-sm">{err}</p>}
    </section>
  );
}

/**
 * Code QR à imprimer pour configurer les iPads : il encode l'URL de l'application.
 * Sur l'iPad, on le scanne (appareil photo), on ouvre l'app puis « Ajouter à
 * l'écran d'accueil » pour l'installer.
 */
function IpadQrSection() {
  const url = typeof window !== 'undefined' ? window.location.origin : '';
  const [dataUrl, setDataUrl] = useState('');

  useEffect(() => {
    if (!url) return;
    void QRCode.toDataURL(url, { width: 512, margin: 2, errorCorrectionLevel: 'M' })
      .then(setDataUrl)
      .catch(() => setDataUrl(''));
  }, [url]);

  function imprimer() {
    const w = window.open('', '_blank', 'width=680,height=900');
    if (!w) return;
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Configuration iPad — Super Show</title></head>` +
        `<body style="font-family:Arial,Helvetica,sans-serif;text-align:center;color:#111;padding:48px 32px;">` +
        `<h1 style="margin:0 0 8px;">Configuration de l'iPad</h1>` +
        `<h2 style="margin:0 0 24px;color:#103fa3;">Application Super Show</h2>` +
        `<p style="font-size:16px;margin:0 auto 24px;max-width:420px;">` +
        `1. Ouvrez l'appareil photo et scannez ce code. 2. Ouvrez le lien. ` +
        `3. Menu Partage → « Ajouter à l'écran d'accueil ».</p>` +
        `<img src="${dataUrl}" alt="QR" style="width:360px;height:360px;" />` +
        `<p style="font-family:monospace;font-size:18px;margin-top:16px;">${url}</p>` +
        `</body></html>`,
    );
    w.document.close();
    w.focus();
    // Laisse l'image se charger avant d'ouvrir la boîte d'impression.
    w.onload = () => w.print();
    setTimeout(() => {
      try {
        w.print();
      } catch {
        /* déjà imprimé via onload */
      }
    }, 400);
  }

  return (
    <section className="bg-card rounded-xl border p-6">
      <h3 className="font-semibold">Configuration des iPads</h3>
      <p className="text-muted-foreground mt-1 text-sm">
        Imprimez ce code QR : sur chaque iPad, scannez-le avec l'appareil photo pour ouvrir
        l'application <b>Super Show</b>, puis « Ajouter à l'écran d'accueil » pour l'installer.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-6">
        {dataUrl ? (
          <img
            src={dataUrl}
            alt={`Code QR vers ${url}`}
            className="h-44 w-44 rounded-lg border bg-white p-2"
          />
        ) : (
          <div className="bg-muted h-44 w-44 animate-pulse rounded-lg" />
        )}
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            Adresse encodée&nbsp;: <span className="text-foreground font-mono">{url}</span>
          </p>
          <button
            type="button"
            onClick={imprimer}
            disabled={!dataUrl}
            className="bg-primary text-primary-foreground h-11 rounded-lg px-5 font-medium disabled:opacity-40"
          >
            Imprimer le code QR
          </button>
        </div>
      </div>
    </section>
  );
}

/** Restauration : remplace les données actuelles par celles d'une sauvegarde. */
function RestoreSection({ backups }: { backups: Backup[] }) {
  const [selected, setSelected] = useState('');
  const [confirm, setConfirm] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const armed = confirm.trim().toUpperCase() === 'RESTAURER';
  const chosen = selected || backups[0]?.name || '';

  const done = (label: string) => {
    setErr(null);
    setMsg(`Restauration réussie (${label}). Rechargement de l'application…`);
    setTimeout(() => window.location.reload(), 1800);
  };

  const fromBackup = useMutation({
    mutationFn: (name: string) =>
      api<{ restored: string }>('/api/admin/restore/backup', {
        method: 'POST',
        body: JSON.stringify({ name, confirm }),
      }),
    onSuccess: (r) => done(r.restored),
    onError: (e: Error) => setErr(e.message),
  });

  const fromUpload = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('confirm', confirm);
      const res = await fetch('/api/admin/restore/upload', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(b.error || `Erreur ${res.status}`);
      }
      return res.json() as Promise<{ restored: string }>;
    },
    onSuccess: (r) => done(r.restored),
    onError: (e: Error) => setErr(e.message),
  });

  const pending = fromBackup.isPending || fromUpload.isPending;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold">Restaurer une sauvegarde</h4>
        <p className="text-muted-foreground text-sm">
          Remplace <b>toutes</b> les données actuelles par celles de la sauvegarde choisie. Sert à
          annuler une réinitialisation faite par erreur, ou à reconsulter un show passé.
        </p>
      </div>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">Depuis une sauvegarde du serveur</span>
        <div className="flex flex-wrap gap-2">
          <select
            value={chosen}
            onChange={(e) => setSelected(e.target.value)}
            className="border-input bg-background h-11 min-w-64 flex-1 rounded-lg border px-3"
          >
            {backups.length === 0 && <option value="">Aucune sauvegarde disponible</option>}
            {backups.map((b) => (
              <option key={b.name} value={b.name}>
                {b.name} — {new Date(b.at).toLocaleString('fr-CA')}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              if (!chosen) return;
              if (!window.confirm(`Restaurer « ${chosen} » ? Les données actuelles seront remplacées.`))
                return;
              fromBackup.mutate(chosen);
            }}
            disabled={!armed || !chosen || pending}
            className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
          >
            {fromBackup.isPending ? 'Restauration…' : 'Restaurer cette sauvegarde'}
          </button>
        </div>
      </label>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">Ou depuis un fichier .sql.gz</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".gz,.sql.gz"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-sm"
          />
          <button
            onClick={() => {
              if (!file) return;
              if (!window.confirm(`Restaurer depuis « ${file.name} » ? Les données actuelles seront remplacées.`))
                return;
              fromUpload.mutate(file);
            }}
            disabled={!armed || !file || pending}
            className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
          >
            {fromUpload.isPending ? 'Restauration…' : 'Restaurer ce fichier'}
          </button>
        </div>
      </label>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">
          Pour confirmer, tapez <code className="bg-muted rounded px-1">RESTAURER</code>
        </span>
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="RESTAURER"
          className="border-input bg-background h-11 w-full max-w-xs rounded-lg border px-3"
        />
      </label>

      {msg && <p className="text-sm font-medium text-green-700 dark:text-green-400">{msg}</p>}
      {err && <p className="text-destructive text-sm">{err}</p>}
    </div>
  );
}

/** Remet à 0 le décompte des visiteurs (étiquettes) — surtout utile après des tests. */
function VisitorsResetSection() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const armed = confirm.trim().toUpperCase() === 'REMETTRE0';
  const reset = useMutation({
    mutationFn: () =>
      api<{ reset: number; backup: string }>('/api/admin/visitors/reset', {
        method: 'POST',
        body: JSON.stringify({ confirm }),
      }),
    onSuccess: (r) => {
      setErr(null);
      setMsg(
        `Décompte des visiteurs remis à 0 (${r.reset} étiquette(s) supprimée(s)). ` +
          `Sauvegarde de sécurité : ${r.backup}.`,
      );
      setConfirm('');
      void qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold">Réinitialiser le décompte des visiteurs</h4>
        <p className="text-muted-foreground text-sm">
          Remet à 0 la tuile <b>Visiteurs (étiquettes)</b> du tableau de bord en supprimant les
          étiquettes imprimées de l'événement en cours. Pratique pour repartir de zéro après des
          tests. Les commandes ne sont pas touchées.
        </p>
      </div>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">
          Pour confirmer, tapez <code className="bg-muted rounded px-1">REMETTRE0</code>
        </span>
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="REMETTRE0"
          className="border-input bg-background h-11 w-full max-w-xs rounded-lg border px-3"
        />
      </label>

      <button
        onClick={() => {
          if (
            !window.confirm(
              "Remettre le décompte des visiteurs à 0 ? Les étiquettes imprimées de l'événement en cours seront supprimées.",
            )
          )
            return;
          reset.mutate();
        }}
        disabled={!armed || reset.isPending}
        className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
      >
        {reset.isPending ? 'Réinitialisation…' : 'Remettre le décompte à 0'}
      </button>
      {msg && <p className="text-sm font-medium text-green-700 dark:text-green-400">{msg}</p>}
      {err && <p className="text-destructive text-sm">{err}</p>}
    </div>
  );
}

/**
 * Purge d'une liste (produits ou clients) : supprime les entrées jamais utilisées
 * dans une commande, désactive celles qui le sont (réactivées au ré-import). Le
 * corps décrit l'effet précis ; `unite` sert aux libellés (« produit »/« client »).
 */
function PurgeSection({
  titre,
  endpoint,
  queryKey,
  unite,
  children,
}: {
  titre: string;
  endpoint: string;
  queryKey: string;
  unite: string;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const overview = useQuery<{ total: number; referenced: number; deletable: number }>({
    queryKey: ['admin', queryKey],
    queryFn: () => api(endpoint),
  });
  const ov = overview.data;
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const armed = confirm.trim().toUpperCase() === 'PURGER';

  const purge = useMutation({
    mutationFn: () =>
      api<{ deleted: number; deactivated: number; backup: string }>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ confirm }),
      }),
    onSuccess: (r) => {
      setErr(null);
      setMsg(
        `Purge effectuée : ${r.deleted} ${unite}(s) supprimé(s), ${r.deactivated} désactivé(s) ` +
          `(déjà dans des commandes). Sauvegarde de sécurité : ${r.backup}.`,
      );
      setConfirm('');
      void qc.invalidateQueries({ queryKey: ['admin', queryKey] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold">{titre}</h4>
        <p className="text-muted-foreground text-sm">{children}</p>
      </div>

      {ov && (
        <div className="bg-muted/40 text-muted-foreground rounded-lg border p-3 text-sm">
          <b className="text-foreground">{ov.total}</b> {unite}·s —{' '}
          <b className="text-foreground">{ov.deletable}</b> seront supprimés,{' '}
          <b className="text-foreground">{ov.referenced}</b> désactivés (déjà dans des commandes).
        </div>
      )}

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">
          Pour confirmer, tapez <code className="bg-muted rounded px-1">PURGER</code>
        </span>
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="PURGER"
          className="border-input bg-background h-11 w-full max-w-xs rounded-lg border px-3"
        />
      </label>

      <button
        onClick={() => {
          if (
            !window.confirm(
              `${titre} ? Les ${unite}s jamais utilisés dans une commande seront supprimés, les autres désactivés.`,
            )
          )
            return;
          purge.mutate();
        }}
        disabled={!armed || purge.isPending}
        className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
      >
        {purge.isPending ? 'Purge…' : titre}
      </button>

      {msg && <p className="text-sm font-medium text-green-700 dark:text-green-400">{msg}</p>}
      {err && <p className="text-destructive text-sm">{err}</p>}
    </div>
  );
}

/** Réinitialisation « nouveau show » : efface tout sauf les comptes admin. */
function ResetSection() {
  const overview = useQuery<ResetOverview>({
    queryKey: ['admin', 'reset'],
    queryFn: () => api('/api/admin/reset'),
  });
  const ov = overview.data;

  const [name, setName] = useState('');
  const [startsAt, setStartsAt] = useState(todayISO);
  const [endsAt, setEndsAt] = useState(() => plusDaysISO(2));
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const expected = ov?.currentEvent?.name ?? 'REINITIALISER';
  const armed =
    name.trim().length > 0 && confirm.trim().toLowerCase() === expected.trim().toLowerCase();

  const reset = useMutation({
    mutationFn: () =>
      api<{ backup: string; newEvent: { name: string }; keptAdmins: number; keptGame: number }>(
        '/api/admin/reset',
        {
          method: 'POST',
          body: JSON.stringify({ confirm, eventName: name.trim(), startsAt, endsAt }),
        },
      ),
    onSuccess: (r) => {
      setErr(null);
      setMsg(
        `Nouveau show « ${r.newEvent.name} » prêt (${r.keptAdmins} admin` +
          `${r.keptGame ? ` + ${r.keptGame} compte jeux` : ''} conservé·s). ` +
          `Sauvegarde de sécurité : ${r.backup}. Rechargement…`,
      );
      setTimeout(() => window.location.reload(), 2000);
    },
    onError: (e: Error) => setErr(e.message),
  });

  const c = ov?.counts;

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold">Réinitialiser pour un nouveau show</h4>
        <p className="text-muted-foreground text-sm">
          Efface <b>toutes</b> les données du show et démarre un événement neuf. Seuls les{' '}
          <b>comptes administrateur</b> et le <b>Compte Jeux</b> (concours) sont conservés.
        </p>
      </div>

      {c && (
        <div className="bg-muted/40 rounded-lg border p-3 text-sm">
          <p className="mb-1 font-medium">Seront effacés :</p>
          <p className="text-muted-foreground">
            {c.orders} commande·s · {c.customers} client·s · {c.products} produit·s · {c.lines} ligne·s ·{' '}
            {c.rewardClaims} réclamation·s cadeau · {c.promotions} promotion·s · {c.reps} compte·s (reps/comptoir)
          </p>
          <p className="mt-1">
            <span className="font-medium text-green-700 dark:text-green-400">Conservé :</span>{' '}
            {ov?.adminCount ?? 0} compte·s administrateur
            {ov?.gameCount ? ` + ${ov.gameCount} compte·s jeux (concours)` : ''}.
          </p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Nom du nouveau show</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Super Show 2027"
            className="border-input bg-background h-11 w-full rounded-lg border px-3"
          />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Date de début</span>
          <input
            type="date"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="border-input bg-background h-11 w-full rounded-lg border px-3"
          />
        </label>
        <label className="space-y-1.5">
          <span className="block text-sm font-medium">Date de fin</span>
          <input
            type="date"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="border-input bg-background h-11 w-full rounded-lg border px-3"
          />
        </label>
      </div>
      <p className="text-muted-foreground text-xs">
        L'année de la date de début fixe le préfixe des numéros de commande (2027 → « SS27-… »).
      </p>

      <label className="block space-y-1.5">
        <span className="text-sm font-medium">
          Pour confirmer, tapez le nom du show actuel :{' '}
          <code className="bg-muted rounded px-1">{expected}</code>
        </span>
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={expected}
          className="border-input bg-background h-11 w-full max-w-md rounded-lg border px-3"
        />
      </label>

      <button
        onClick={() => {
          if (!window.confirm('Dernière confirmation : effacer toutes les données du show (sauf les admins) ?'))
            return;
          reset.mutate();
        }}
        disabled={!armed || reset.isPending}
        className="bg-destructive h-11 rounded-lg px-5 font-medium text-white disabled:opacity-40"
      >
        {reset.isPending ? 'Réinitialisation…' : 'Réinitialiser et démarrer le nouveau show'}
      </button>

      {msg && <p className="text-sm font-medium text-green-700 dark:text-green-400">{msg}</p>}
      {err && <p className="text-destructive text-sm">{err}</p>}
    </div>
  );
}
